/**
 * Copyright © 2026 Indisea Software LLC. All rights reserved.
 *
 * Proprietary and confidential. Licensed, not sold. Use is subject to the
 * license agreement between Indisea Software LLC and the licensee.
 * See LICENSE for full terms.
 *
 * SPDX-License-Identifier: LicenseRef-Indisea-Proprietary
 */

    import React, { useEffect, useRef, useState } from "react";
    import {
        Box,
        Button,
        CircularProgress,
        Typography,
        TextField,
        IconButton,
        Tooltip,
        Paper,
        Table,
        TableBody,
        TableCell,
        TableContainer,
        TableHead,
        TableRow,
        TablePagination,
        Autocomplete,
        Alert,
        Stack,
        Backdrop,
        Chip,
        Snackbar,
        Dialog,
        DialogTitle,
        DialogContent,
        DialogActions,
        LinearProgress,
    } from "@mui/material";
    import RefreshIcon from "@mui/icons-material/Refresh";
    import EditIcon from "@mui/icons-material/Edit";
    import CloudDoneOutlinedIcon from "@mui/icons-material/CloudDoneOutlined";
    import CheckCircleIcon from "@mui/icons-material/CheckCircle";
    import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
    import DownloadIcon from '@mui/icons-material/Download';
    import UploadIcon from '@mui/icons-material/Upload';
    import apiHeaders from "./apiHeaders";
    import { handleSessionExpiredClose } from "./logout";
    import ImportProgress from "./components/ImportCustomerProgress";
    import { safeJson, extractErrorMessage } from "./errorUtils";

    const ROW_STATUS = {
        SAVED: "saved",
        MODIFIED: "modified",
        IMPORTED: "imported",
        ERROR: "error",
    };

    // External Source (posting mode) is a 3-way string at the API level - "Standard" (calculate +
    // post), "PostOnly" (post without recalculating), or "NoPosting" (skip entirely). The actual
    // set of options (canonical value + display label) is loaded from GET api/customer/posting-modes
    // (see postingModeOptions state below) rather than hardcoded here, so a future posting mode
    // never requires a frontend change. This is only the emergency fallback used if that fetch fails.
    const DEFAULT_POSTING_MODE_OPTIONS = [{ value: "Standard", label: "Standard" }];

    // Default Marketplace value used whenever a row has no (or an empty) selection.
    const DEFAULT_MARKETPLACE = "None";
    const normalizeMarketplace = (value) => value || DEFAULT_MARKETPLACE;

    function TaxCodeMapping() {
        const [items, setItems] = useState([]);
        const [taxCodes, setTaxCodes] = useState([]);
        const [marketplaces, setMarketplaces] = useState([DEFAULT_MARKETPLACE]);
        const [postingModeOptions, setPostingModeOptions] = useState(DEFAULT_POSTING_MODE_OPTIONS);
        const [loading, setLoading] = useState(false);
        const [saving, setSaving] = useState(false);
        const [refreshing, setRefreshing] = useState(false);
        const [filterText, setFilterText] = useState("");
        const [unsavedChanges, setUnsavedChanges] = useState(false);
        const [message, setMessage] = useState({ type: "", text: "" });
        const [error, setError] = useState("");

        const [page, setPage] = useState(0);
        const [rowsPerPage, setRowsPerPage] = useState(10);
        const [rowStatuses, setRowStatuses] = useState({});
        const [statusFilter, setStatusFilter] = useState("all");

        // Server-side pagination state. When `serverPaged` is true, `items` holds exactly one
        // page's worth of rows fetched from the DB (via pageNumber/pageSize) and `totalCount` is
        // the server's row count across every page - this is the common "just browsing" case,
        // and it's what keeps a DB query from ever having to load a tenant's entire customer list
        // just to render one page. Filtering/searching needs every row to match against, so the
        // moment the user filters we drop into "full list" mode (serverPaged=false): `items` then
        // holds the complete dataset and pagination becomes the old client-side slice again.
        const [totalCount, setTotalCount] = useState(0);
        const [serverPaged, setServerPaged] = useState(true);

        // True only while a page-turn/search/mode-switch fetch is in flight - unlike `loading`
        // (which unmounts the whole view), this drives a small inline indicator so the search box
        // and table stay mounted and interactive while the debounced search request is running.
        const [tableLoading, setTableLoading] = useState(false);

        // Holds the pending debounce timer for search-as-you-type, so a search request only ever
        // fires once typing pauses instead of on every keystroke.
        const searchDebounceRef = useRef(null);

        // Edits survive page/mode changes independently of `items` (which gets replaced by every
        // page fetch): keyed by customerId, holding the latest edited version of each row a user
        // has touched. This is exactly what Save submits - only the rows that actually changed,
        // never the full list - so paging away from an edited row and back never loses the edit,
        // and Save stays correct/cheap regardless of how many pages were browsed. This works
        // because the backend's SaveAllAsync only upserts the rows it's given; it never deletes
        // rows that are simply absent from the payload.
        const [editedRows, setEditedRows] = useState({});

        // Import and Error Modal States
        const [status, setStatus] = useState("idle");
        const [data, setData] = useState(null);
        const [orphanErrors, setOrphanErrors] = useState([]);
        const [errorDetail, setErrorDetail] = useState(null);
        const [isOpen, setIsOpen] = useState(false);
        const [severity, setSeverity] = useState("success");
        const [alertMessage, setAlertMessage] = useState("");

        const realmId = sessionStorage.getItem("realmId");
        const authHeaders = { ...apiHeaders, Authorization: `${sessionStorage.getItem("token")}` };

        const showNotification = (type, msg) => {
            setSeverity(type);
            setAlertMessage(msg);
            setIsOpen(true);
        };

        const handleClose = () => setIsOpen(false);

        // Fetches a single page of customer mappings, optionally narrowed by a search term (which
        // is applied at the SQL level server-side - see CustomerEntityMappingRepository). Used for
        // the default "just browsing" view, including while the user is actively searching.
        const fetchMappingPage = async (pageNumber, pageSizeArg, refresh, searchTermArg = "") => {
            const searchParam = searchTermArg ? `&searchTerm=${encodeURIComponent(searchTermArg)}` : "";
            const res = await fetch(
                `${process.env.REACT_APP_CONNECTOR_API_URL}/api/customer/mapping?realmId=${realmId}&refresh=${refresh}&pageNumber=${pageNumber}&pageSize=${pageSizeArg}${searchParam}`,
                { headers: authHeaders }
            );
            if (res.status === 401) {
                handleSessionExpiredClose();
                return null;
            }
            if (!res.ok) {
                throw new Error(await extractErrorMessage(res, "Something went wrong. Please try again later."));
            }
            return await safeJson(res); // { items, totalCount, pageNumber, pageSize }
        };

        // Fetches one page (with the given search term) and applies the result, using the
        // lightweight `tableLoading` indicator rather than the full-page spinner - shared by page
        // changes, rows-per-page changes, debounced search, and returning from full-list mode.
        const fetchServerPage = async (pageNumber, pageSizeArg, searchTermArg) => {
            setTableLoading(true);
            try {
                const pageResult = await fetchMappingPage(pageNumber, pageSizeArg, false, searchTermArg);
                setItems(Array.isArray(pageResult?.items) ? pageResult.items : []);
                setTotalCount(typeof pageResult?.totalCount === "number" ? pageResult.totalCount : 0);
                setServerPaged(true);
            } catch (err) {
                setError(err.message || "Error fetching data");
            } finally {
                setTableLoading(false);
            }
        };

        // Fetches the complete customer mapping list (no page params). Needed whenever filtering
        // must match against every row, not just whichever page happens to be loaded.
        const fetchAllMappings = async (refresh) => {
            const res = await fetch(
                `${process.env.REACT_APP_CONNECTOR_API_URL}/api/customer/mapping?realmId=${realmId}&refresh=${refresh}`,
                { headers: authHeaders }
            );
            if (res.status === 401) {
                handleSessionExpiredClose();
                return [];
            }
            if (!res.ok) {
                throw new Error(await extractErrorMessage(res, "Something went wrong. Please try again later."));
            }
            return await safeJson(res); // plain array, since no page params were sent
        };

        const fetchData = async (refresh = false) => {
            if (!realmId) {
                setError("Missing realm information. Please log in again.");
                setItems([]);
                setTaxCodes([]);
                return;
            }

            setLoading(true);
            setError("");

            try {
                const [pageRes, taxCodesRes, marketplaceRes, postingModesRes] = await Promise.all([
                    fetch(`${process.env.REACT_APP_CONNECTOR_API_URL}/api/customer/mapping?realmId=${realmId}&refresh=${refresh}&pageNumber=1&pageSize=${rowsPerPage}`, {
                        headers: authHeaders,
                    }),
                    fetch(`${process.env.REACT_APP_CONNECTOR_API_URL}/api/customer/zamp?realmId=${realmId}`, {
                        headers: authHeaders,
                    }),
                    fetch(`${process.env.REACT_APP_CONNECTOR_API_URL}/api/customer/marketplace`, {
                        headers: authHeaders,
                    }),
                    fetch(`${process.env.REACT_APP_CONNECTOR_API_URL}/api/customer/posting-modes`, {
                        headers: authHeaders,
                    }),
                ]);

                if (pageRes.status === 401 || taxCodesRes.status === 401 || marketplaceRes.status === 401 || postingModesRes.status === 401) {
                    handleSessionExpiredClose();
                    setItems([]);
                    setTaxCodes([]);
                    return;
                }

                if (!pageRes.ok || !taxCodesRes.ok) {
                    const failedRes = !pageRes.ok ? pageRes : taxCodesRes;
                    throw new Error(await extractErrorMessage(failedRes, "Something went wrong. Please try again later."));
                }

                const [pageResult, taxCodesData] = await Promise.all([safeJson(pageRes), safeJson(taxCodesRes)]);
                // Marketplace and posting-mode options are non-critical: fall back to a safe default if unavailable.
                const marketplaceData = marketplaceRes.ok ? await safeJson(marketplaceRes) : [];
                const postingModesData = postingModesRes.ok ? await safeJson(postingModesRes) : [];

                setItems(Array.isArray(pageResult?.items) ? pageResult.items : []);
                setTotalCount(typeof pageResult?.totalCount === "number" ? pageResult.totalCount : 0);
                setServerPaged(true);
                setPage(0);
                setEditedRows({});
                setTaxCodes(taxCodesData || []);
                setMarketplaces(Array.isArray(marketplaceData) && marketplaceData.length ? marketplaceData : [DEFAULT_MARKETPLACE]);
                setPostingModeOptions(Array.isArray(postingModesData) && postingModesData.length ? postingModesData : DEFAULT_POSTING_MODE_OPTIONS);
                setError("");

            } catch (err) {
                setError(err.message || "Error fetching data");
                setItems([]);
                setTaxCodes([]);
            } finally {
                setLoading(false);
                setRefreshing(false);
            }
        };

        useEffect(() => {
            fetchData();
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [realmId]);

        // Switches the grid from a single server page into "full list" mode: needed only for the
        // Modified/Imported/Error status chips, since those are local unsaved-edit states with no
        // DB column to filter on - the server can't page/filter by them, so matching against them
        // requires every row to be loaded. Free-text search never needs to call this itself (see
        // handleFilterTextChange below) - it stays server-paginated only while nothing has already
        // pulled the full list into memory (a status filter, or an Import).
        const switchToFullListMode = async () => {
            setTableLoading(true);
            try {
                const fullList = await fetchAllMappings(false);
                setItems(fullList || []);
                setServerPaged(false);
                setPage(0);
            } catch (err) {
                setError(err.message || "Error fetching data");
            } finally {
                setTableLoading(false);
            }
        };

        // Clears any pending debounced search fetch when the component unmounts.
        useEffect(() => {
            return () => {
                if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
            };
        }, []);

        // Patch one or more fields of a single row, flag it as locally modified, and record the
        // edit in the diff buffer that Save actually submits (independent of the current page).
        const handleFieldChange = (customerId, patch) => {
            setMessage({ type: "", text: "" });
            setItems(prev => prev.map(it => (it.customerId === customerId ? { ...it, ...patch } : it)));
            setEditedRows(prev => {
                const base = prev[customerId] || items.find(it => it.customerId === customerId) || {};
                return { ...prev, [customerId]: { ...base, ...patch } };
            });
            setRowStatuses(prev => ({
                ...prev,
                [customerId]: { status: ROW_STATUS.MODIFIED, tooltip: "Modified — not yet saved" },
            }));
            setUnsavedChanges(true);
        };

        const handleZampEntityChange = (customerId, newEntity) =>
            handleFieldChange(customerId, { zampEntity: newEntity });

        // External Source (posting mode) comes back from the API as one of "Standard"/"PostOnly"/
        // "NoPosting"; the dropdown shows the friendlier label from postingModeOptions (loaded from
        // GET api/customer/posting-modes), mapped back to its canonical value on selection.
        const externalSourceLabel = (value) =>
            postingModeOptions.find((o) => o.value === value)?.label
            ?? postingModeOptions[0]?.label
            ?? value;

        const handleExternalSourceChange = (customerId, label) =>
            handleFieldChange(customerId, {
                externalSource: postingModeOptions.find((o) => o.label === label)?.value ?? postingModeOptions[0]?.value,
            });

        // Marketplace is a string; clearing the dropdown resets it to the default ("None").
        const handleMarketplaceChange = (customerId, newValue) =>
            handleFieldChange(customerId, { marketplace: normalizeMarketplace(newValue) });

        const handleSave = async () => {
            if (!realmId) {
                setError("Missing realm information. Please log in again.");
                return;
            }
            const dirtyRows = Object.values(editedRows);
            if (dirtyRows.length === 0) {
                setUnsavedChanges(false);
                return;
            }
            setSaving(true);
            try {
                // Only the rows that actually changed are submitted - never the whole dataset -
                // so Save stays correct and cheap no matter how many pages were browsed.
                const response = await fetch(`${process.env.REACT_APP_CONNECTOR_API_URL}/api/customer/mapping?realmId=${realmId}`, {
                    method: "POST",
                    headers: { ...apiHeaders, Authorization: `${sessionStorage.getItem("token")}`, "Content-Type": "application/json" },
                    body: JSON.stringify(dirtyRows),
                });
                if (response.status === 401) {
                    handleSessionExpiredClose();
                    return;
                }
                if (!response.ok) throw new Error("Failed to save mappings");

                setMessage({ type: "success", text: "Customer entity mappings saved successfully!" });
                setUnsavedChanges(false);
                setRowStatuses({});
                setEditedRows({});
            } catch (err) {
                setMessage({ type: "error", text: err.message || "Error saving mappings" });
            } finally {
                setSaving(false);
            }
        };

        const handleRefresh = () => {
            setMessage({ type: "", text: "" });
            setRefreshing(true);
            setFilterText("");
            setStatusFilter("all");
            if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
            fetchData(true);

            // Reset Import Data
            setData(null);
            setStatus("idle");

            // FIX: Reset all status trackers, errors, and unsaved changes
            setRowStatuses({});
            setOrphanErrors([]);
            setUnsavedChanges(false);
        };

        const handleChangePage = async (event, newPage) => {
            setPage(newPage);
            if (serverPaged) {
                await fetchServerPage(newPage + 1, rowsPerPage, filterText.trim());
            }
        };

        const handleChangeRowsPerPage = async (event) => {
            const newRowsPerPage = parseInt(event.target.value, 10);
            setRowsPerPage(newRowsPerPage);
            setPage(0);
            if (serverPaged) {
                await fetchServerPage(1, newRowsPerPage, filterText.trim());
            }
        };

        // Free-text search stays server-paginated: it's debounced so a fetch only fires once
        // typing pauses. It's skipped entirely whenever we're not server-paged - that covers both
        // ways the grid ends up holding the complete list in memory (a status filter switch via
        // switchToFullListMode, or a just-completed Import): filteredItems already re-applies
        // filterText against whatever's already loaded, so a server round trip here would only
        // serve to silently discard that in-memory view (and any unsaved edits/import results
        // with it) in favor of a freshly-fetched, unfiltered-by-those-edits server page.
        const handleFilterTextChange = (value) => {
            setFilterText(value);
            setPage(0);
            if (message.text) setMessage({ type: "", text: "" });

            if (!serverPaged) return;

            if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
            searchDebounceRef.current = setTimeout(() => {
                fetchServerPage(1, rowsPerPage, value.trim());
            }, 300);
        };

        const handleStatusFilterChange = (newStatus) => {
            setStatusFilter(newStatus);
            setPage(0);
            if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
            if (newStatus === "all") {
                if (unsavedChanges) {
                    // The full list (with pending Import/edit results) is already loaded in
                    // memory - clearing the status filter just needs filteredItems to show every
                    // row again. Fetching from the server here would silently replace that view
                    // with stale DB data until Save or Refresh runs, hiding unsaved work.
                    return;
                }
                // Nothing unsaved: safe to resume efficient server-paged browsing, honoring
                // whatever search text is currently in the box.
                fetchServerPage(1, rowsPerPage, filterText.trim());
            } else {
                switchToFullListMode();
            }
        };

        const handleExport = async () => {
            try {
                const response = await fetch(`${process.env.REACT_APP_CONNECTOR_API_URL}/api/customer/export?realmId=${realmId}`, {
                    method: 'GET',
                });

                if (!response.ok) {
                    // Surfaces the backend's own message when available (e.g. "Zamp API token is
                    // not configured...") instead of a generic failure notice, so the user knows
                    // what to fix.
                    throw new Error(await extractErrorMessage(response, 'Export failed'));
                }

                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);

                const link = document.createElement('a');
                const disposition = response.headers.get('Content-Disposition');
                const formattedDate = new Date().toISOString().split('T')[0];
                let fileName = `customer_entity_data_${formattedDate}.xlsx`;
                if (disposition && disposition.includes('filename=')) {
                    const match = disposition.match(/filename="?([^"]+)"?/);
                    if (match && match[1]) {
                        fileName = match[1];
                    }
                }
                link.href = url;
                link.download = fileName;
                document.body.appendChild(link);
                link.click();
                link.remove();

                window.URL.revokeObjectURL(url);
            } catch (err) {
                showNotification('error', err.message || 'Unexpected error occurred');
            }
        };

        const handleImport = async (event) => {
            setMessage({ type: "", text: "" });
            const file = event.target.files[0];
            if (!file) return;

            const formData = new FormData();
            formData.append('file', file);
            setStatus("loading");
            setRowStatuses({});
            setOrphanErrors([]);
            setStatusFilter("all");
            setEditedRows({});
            try {
                const response = await fetch(`${process.env.REACT_APP_CONNECTOR_API_URL}/api/customer/import?realmId=${realmId}`, {
                    method: 'POST',
                    body: formData,
                });

                if (response.status === 400) {
                    // A 400 here is either a validation failure or - most commonly - the
                    // backend's UserActionRequiredException for a missing Zamp API token (see
                    // ExceptionHandlingMiddleware), both returned as JSON `{ message }`. Parsing it
                    // instead of showing the raw response body avoids leaking a stringified JSON
                    // blob to the user.
                    showNotification('error', await extractErrorMessage(response, 'Invalid file format'));
                    return;
                }

                if (!response.ok) {
                    showNotification('error', await extractErrorMessage(response, 'Import failed'));
                    return;
                }

                const json = await response.json();
                setData(json);

                const newStatuses = {};
                const newOrphanErrors = [];

                const importedMappings = json.customerMappings || json.taxCodeMappings || [];

                // The backend tells us exactly which rows it matched and updated, by customerId
                // (see ImportProgress.UpdatedRecordIds on the backend). This is the only reliable
                // signal: diffing the import result against `items` (as this used to do) breaks
                // for a large import, because `items` is usually just whichever single page
                // (25-100 rows) was loaded before Import ran, not the full dataset - so a 60k-row
                // import would find almost no "changed" rows, editedRows would end up empty, and
                // Save would silently have nothing to submit.
                const updatedIds = new Set(json.updatedRecordIds || []);
                importedMappings.forEach(newItem => {
                    if (updatedIds.has(newItem.customerId)) {
                        newStatuses[newItem.customerId] = {
                            status: ROW_STATUS.IMPORTED,
                            tooltip: "Updated via import — not yet saved",
                        };
                    }
                });

                if (json.invalidProducts?.length > 0) {
                    json.invalidProducts.forEach((prod, i) => {
                        newOrphanErrors.push({
                            customerId: `orphan-prod-${i}`,
                            customerName: prod,
                            zampEntity: "",
                            _orphan: true,
                            _error: "Customer not found in the system",
                        });
                    });
                }

                if (json.invalidTaxCodes?.length > 0) {
                    json.invalidTaxCodes.forEach((tc, i) => {
                        newOrphanErrors.push({
                            customerId: `orphan-tc-${i}`,
                            customerName: "",
                            zampEntity: tc,
                            _orphan: true,
                            _error: `Invalid Zamp Entity: "${tc}"`,
                        });
                    });
                }

                setRowStatuses(newStatuses);
                setOrphanErrors(newOrphanErrors);

                if (json.summary?.updatedCount) {
                    // Import only stages the change in memory (it isn't persisted server-side
                    // until Save runs), so the diff buffer must be seeded with exactly the rows
                    // Import changed - the same set flagged "Imported" above.
                    const importedEdits = {};
                    importedMappings.forEach(newItem => {
                        if (newStatuses[newItem.customerId]) {
                            importedEdits[newItem.customerId] = newItem;
                        }
                    });
                    setItems(importedMappings || []);
                    setServerPaged(false); // we already have the complete dataset in hand
                    setTotalCount((importedMappings || []).length);
                    setPage(0);
                    setEditedRows(importedEdits);
                    setUnsavedChanges(true);
                }
                setStatus("completed");
            } catch (err) {
                setData(null);
                setStatus("idle");
                showNotification('error', 'Unexpected error occurred');
            } finally {
                event.target.value = null;
            }
        };

        const handleDismiss = () => {
            setData(null);
            setStatus("idle");
            setOrphanErrors([]);
            setRowStatuses(prev => {
                const next = {};
                Object.entries(prev).forEach(([k, v]) => {
                    if (v.status !== ROW_STATUS.ERROR) next[k] = v;
                });
                return next;
            });
            setStatusFilter("all");
        };

        const getRowStatusIcon = (item) => {
            const info = rowStatuses[item.customerId];
            if (!info || info.status === ROW_STATUS.SAVED) {
                return (
                    <Tooltip title="Saved" placement="left">
                        <CloudDoneOutlinedIcon sx={{ fontSize: 16, color: "grey.400" }} />
                    </Tooltip>
                );
            }
            if (info.status === ROW_STATUS.MODIFIED) {
                return (
                    <Tooltip title={info.tooltip} placement="left">
                        <EditIcon sx={{ fontSize: 16, color: "warning.main" }} />
                    </Tooltip>
                );
            }
            if (info.status === ROW_STATUS.IMPORTED) {
                return (
                    <Tooltip title={info.tooltip} placement="left">
                        <CheckCircleIcon sx={{ fontSize: 16, color: "success.main" }} />
                    </Tooltip>
                );
            }
            if (info.status === ROW_STATUS.ERROR) {
                return (
                    <Tooltip title={info.tooltip} placement="left">
                        <ErrorOutlineIcon
                            sx={{ fontSize: 16, color: "error.main", cursor: "pointer" }}
                            onClick={() => setErrorDetail(info)}
                        />
                    </Tooltip>
                );
            }
            return null;
        };

        const hasActiveStatuses = Object.keys(rowStatuses).length > 0;
        const totalRowCount = serverPaged ? totalCount : items.length;

        const statusCounts = (() => {
            if (!hasActiveStatuses) return {};
            // NOTE: while serverPaged, this only reflects the currently loaded page - the true
            // across-realm counts require full-list mode (see switchToFullListMode). Save itself
            // is unaffected, since it always submits every entry in `editedRows` regardless of
            // which page is currently in view.
            const counts = { [ROW_STATUS.SAVED]: 0, [ROW_STATUS.MODIFIED]: 0, [ROW_STATUS.IMPORTED]: 0, [ROW_STATUS.ERROR]: 0 };
            items.forEach(it => {
                const s = rowStatuses[it.customerId]?.status || ROW_STATUS.SAVED;
                counts[s]++;
            });
            return counts;
        })();

        // Overlays any pending (unsaved) edits onto whatever page/list is currently loaded, so a
        // previously edited row still shows the user's edit even after paging away and back.
        const displayItems = items.map(it => (editedRows[it.customerId] ? { ...it, ...editedRows[it.customerId] } : it));

        const filteredItems = (() => {
            // While serverPaged, the text search already ran server-side (see
            // handleFilterTextChange) - re-applying it here against `filterText` would be racy
            // during the debounce window (the input updates immediately; `items` only refreshes
            // once the debounced fetch resolves), so skip it and trust whatever the server returned.
            const search = serverPaged ? "" : filterText.toLowerCase();
            const base = displayItems.filter(it => {
                const matchesText = it.customerName.toLowerCase().includes(search) ||
                                (it.zampEntity || "").toLowerCase().includes(search) ||
                                normalizeMarketplace(it.marketplace).toLowerCase().includes(search);
                if (!matchesText) return false;
                if (statusFilter === "all") return true;
                const s = rowStatuses[it.customerId]?.status || ROW_STATUS.SAVED;
                return s === statusFilter;
            });
            if (statusFilter === ROW_STATUS.ERROR && orphanErrors.length > 0) {
                const filtered = orphanErrors.filter(o =>
                    o.customerName.toLowerCase().includes(search) ||
                    (o.zampEntity || "").toLowerCase().includes(search)
                );
                return [...base, ...filtered];
            }
            return base;
        })();

        // When serverPaged, `filteredItems` already IS exactly one page's worth of rows fetched
        // from the DB, so it's rendered as-is; otherwise (full list loaded) it's sliced client-side.
        const rowsToRender = serverPaged
            ? filteredItems
            : filteredItems.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

        if (loading) return (
            <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", height: "calc(100vh - 64px)" }}>
                <CircularProgress size={48} thickness={4} />
                <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
                    Loading mappings...
                </Typography>
            </Box>
        );

        return (
            <Box display="flex" flexDirection="column" gap={2} p={2}>
                {/* Page Heading */}
                <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold", textAlign: "center" }}>
                    Customer Entity Mapping Utility
                </Typography>

                {/* Table context setup wrapper */}
                <Paper elevation={6} sx={{ boxShadow: 4, borderRadius: 2, p: 4, maxWidth: 1250, width: "100%", mx: "auto", mt: 1 }}>
                    <Stack spacing={2} direction="column" sx={{ width: "100%", mt: 2 }}>

                        {/* Filter, Import, Export & Refresh Controls */}
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, gap: 2 }}>
                            <TextField
                                size="small"
                                label="Filter by customer name, zamp entity or marketplace"
                                placeholder="Start typing to filter by customer name, zamp entity or marketplace"
                                value={filterText}
                                onChange={e => handleFilterTextChange(e.target.value)}
                                sx={{ flexBasis: "50%" }}
                            />
                            <Box display="flex" justifyContent="flex-end" alignItems="center" gap={2}>
                                <input
                                    type="file"
                                    accept=".xlsx"
                                    style={{ display: 'none' }}
                                    id="import-file"
                                    onChange={handleImport}
                                />
                                <label htmlFor="import-file">
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        component="span"
                                        startIcon={<UploadIcon />}
                                    >
                                        Import
                                    </Button>
                                </label>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    startIcon={<DownloadIcon />}
                                    onClick={handleExport}
                                >
                                    Export
                                </Button>
                            </Box>
                            <Tooltip title="Refresh QBO customers">
                                <IconButton
                                    type="button"
                                    className="zamp-icon-action"
                                    onClick={handleRefresh}
                                    disabled={refreshing}
                                >
                                    <RefreshIcon sx={{ fontSize: 24, animation: refreshing ? "spin 1s linear infinite" : "none" }} />
                                </IconButton>
                            </Tooltip>
                        </Box>

                        {/* Import Status Tracking Blocks */}
                        {status === "loading" && (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 2 }}>
                                <CircularProgress size={24} />
                                <Typography variant="body2">Loading import progress...</Typography>
                            </Box>
                        )}

                        {status === "completed" && (
                            <ImportProgress data={data} onDismiss={handleDismiss} />
                        )}

                        {/* Status Filter Chips */}
                        {hasActiveStatuses && (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                                <Typography variant="body2" color="text.secondary" sx={{ mr: 0.5 }}>Status:</Typography>
                                <Chip
                                    label={`All (${totalRowCount})`}
                                    size="small"
                                    variant={statusFilter === "all" ? "filled" : "outlined"}
                                    onClick={() => handleStatusFilterChange("all")}
                                />
                                {(statusCounts[ROW_STATUS.IMPORTED] || 0) > 0 && (
                                    <Chip
                                        icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                                        label={statusCounts[ROW_STATUS.IMPORTED]}
                                        size="small"
                                        variant={statusFilter === ROW_STATUS.IMPORTED ? "filled" : "outlined"}
                                        color="success"
                                        onClick={() => handleStatusFilterChange(ROW_STATUS.IMPORTED)}
                                    />
                                )}
                                {(statusCounts[ROW_STATUS.MODIFIED] || 0) > 0 && (
                                    <Chip
                                        icon={<EditIcon sx={{ fontSize: 14 }} />}
                                        label={statusCounts[ROW_STATUS.MODIFIED]}
                                        size="small"
                                        variant={statusFilter === ROW_STATUS.MODIFIED ? "filled" : "outlined"}
                                        color="warning"
                                        onClick={() => handleStatusFilterChange(ROW_STATUS.MODIFIED)}
                                    />
                                )}
                                {((statusCounts[ROW_STATUS.ERROR] || 0) > 0 || orphanErrors.length > 0) && (
                                    <Chip
                                        icon={<ErrorOutlineIcon sx={{ fontSize: 14 }} />}
                                        label={(statusCounts[ROW_STATUS.ERROR] || 0) + orphanErrors.length}
                                        size="small"
                                        variant={statusFilter === ROW_STATUS.ERROR ? "filled" : "outlined"}
                                        color="error"
                                        onClick={() => handleStatusFilterChange(ROW_STATUS.ERROR)}
                                    />
                                )}
                            </Box>
                        )}

                        {error && (
                            <Alert severity="error" onClose={() => setError("")}>{error}</Alert>
                        )}

                        {/* Core Mapping Table */}
                        {tableLoading && <LinearProgress />}
                        <TableContainer sx={{ border: "1px solid rgba(224, 224, 224, 1)", borderRadius: 1 }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ height: 50, backgroundColor: "grey.400" }}>
                                        <TableCell sx={{ fontWeight: "bold", fontSize: 16, width: "28%", borderRight: "1px solid rgba(224, 224, 224, 1)" }}>Customer Name</TableCell>
                                        <TableCell sx={{ fontWeight: "bold", fontSize: 16, width: "27%", borderRight: "1px solid rgba(224, 224, 224, 1)" }}>Zamp Entity</TableCell>
                                        <TableCell sx={{ fontWeight: "bold", fontSize: 16, width: "18%", borderRight: "1px solid rgba(224, 224, 224, 1)" }}>External Source</TableCell>
                                        <TableCell sx={{ fontWeight: "bold", fontSize: 16, width: "27%" }}>Marketplace</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {rowsToRender.map((item, index) => {
                                        const isOrphan = !!item._orphan;
                                        const info = isOrphan ? null : rowStatuses[item.customerId];
                                        const s = isOrphan ? ROW_STATUS.ERROR : info?.status;
                                        const rowBg = s === ROW_STATUS.IMPORTED
                                            ? (index % 2 === 0 ? "#f6faf3" : "#eef5ea")
                                            : s === ROW_STATUS.ERROR
                                                ? (index % 2 === 0 ? "#fef7f7" : "#fdf0f0")
                                                : s === ROW_STATUS.MODIFIED
                                                    ? (index % 2 === 0 ? "#fffbf0" : "#fff7e6")
                                                    : (index % 2 === 0 ? "white" : "grey.100");

                                        return (
                                            <TableRow key={item.customerId} sx={{ backgroundColor: rowBg }}>
                                                <TableCell sx={{
                                                    borderRight: "1px solid rgba(224, 224, 224, 1)",
                                                    maxWidth: 300,
                                                    whiteSpace: "nowrap",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                }}>
                                                    <Tooltip
                                                        title={item.customerName}
                                                        placement="bottom"
                                                        slotProps={{
                                                            popper: { modifiers: [{ name: "offset", options: { offset: [0, 8] } }] },
                                                            tooltip: { sx: { color: "white", fontSize: "0.8rem", borderRadius: 1, px: 1.5, py: 0.5 } }
                                                        }}
                                                    >
                                                        <Box component="span" sx={{ display: "inline-block", maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                            {item.customerName}
                                                        </Box>
                                                    </Tooltip>
                                                </TableCell>
                                                {/* Zamp Entity */}
                                                <TableCell sx={{ borderRight: "1px solid rgba(224, 224, 224, 1)" }}>
                                                    {isOrphan ? (
                                                        <Typography variant="body2" color="text.disabled" sx={{ fontSize: 13, fontStyle: "italic", pl: 1 }}>
                                                            {item.zampEntity || "-"}
                                                        </Typography>
                                                    ) : (
                                                        <Autocomplete
                                                            options={taxCodes}
                                                            getOptionLabel={(option) => (typeof option === "string" ? option : option.code)}
                                                            renderOption={(props, option) => <li {...props}>{option.code}</li>}
                                                            value={taxCodes.find(tc => tc.code === item.zampEntity) || item.zampEntity || ""}
                                                            onChange={(e, newValue) => {
                                                                handleZampEntityChange(item.customerId, newValue ? (typeof newValue === "string" ? newValue : newValue.code) : "");
                                                            }}
                                                            renderInput={(params) => (
                                                                <TextField {...params} size="small" placeholder="Start typing to select zamp entity" />
                                                            )}
                                                            fullWidth
                                                        />
                                                    )}
                                                </TableCell>

                                                {/* External Source — posting mode, options loaded dynamically from GET api/customer/posting-modes */}
                                                <TableCell sx={{ borderRight: "1px solid rgba(224, 224, 224, 1)" }}>
                                                    {isOrphan ? (
                                                        <Typography variant="body2" color="text.disabled" sx={{ fontSize: 13, fontStyle: "italic", pl: 1 }}>
                                                            -
                                                        </Typography>
                                                    ) : (
                                                        <Autocomplete
                                                            options={postingModeOptions.map((o) => o.label)}
                                                            value={externalSourceLabel(item.externalSource)}
                                                            onChange={(e, newValue) => handleExternalSourceChange(item.customerId, newValue)}
                                                            isOptionEqualToValue={(option, value) => option === value}
                                                            disableClearable
                                                            renderInput={(params) => (
                                                                <TextField {...params} size="small" placeholder="Select" />
                                                            )}
                                                            fullWidth
                                                        />
                                                    )}
                                                </TableCell>

                                                {/* Marketplace — string dropdown with search; clearing resets to "None" */}
                                                <TableCell>
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                                        <Box sx={{ flex: 1 }}>
                                                            {isOrphan ? (
                                                                <Typography variant="body2" color="text.disabled" sx={{ fontSize: 13, fontStyle: "italic", pl: 1 }}>
                                                                    -
                                                                </Typography>
                                                            ) : (() => {
                                                                const currentMarketplace = normalizeMarketplace(item.marketplace);
                                                                const isUnknownMarketplace = currentMarketplace !== DEFAULT_MARKETPLACE && !marketplaces.includes(currentMarketplace);
                                                                return (
                                                                    <Autocomplete
                                                                        options={marketplaces}
                                                                        value={currentMarketplace}
                                                                        onChange={(e, newValue) => handleMarketplaceChange(item.customerId, newValue)}
                                                                        isOptionEqualToValue={(option, value) => option === value}
                                                                        renderInput={(params) => (
                                                                            <TextField
                                                                                {...params}
                                                                                size="small"
                                                                                placeholder="Start typing to select marketplace"
                                                                                error={isUnknownMarketplace}
                                                                                helperText={isUnknownMarketplace ? `"${currentMarketplace}" is not an available marketplace` : undefined}
                                                                            />
                                                                        )}
                                                                        fullWidth
                                                                    />
                                                                );
                                                            })()}
                                                        </Box>
                                                        <Box sx={{ flexShrink: 0, width: 20, display: "flex", justifyContent: "center" }}>
                                                            {isOrphan ? (
                                                                <Tooltip title={item._error} placement="left">
                                                                    <ErrorOutlineIcon
                                                                        sx={{ fontSize: 14, color: "error.main", cursor: "pointer" }}
                                                                        onClick={() => setErrorDetail({
                                                                            error: item._error,
                                                                            rowData: { itemCode: item.customerId, itemName: item.customerName, taxCode: item.zampEntity },
                                                                        })}
                                                                    />
                                                                </Tooltip>
                                                            ) : getRowStatusIcon(item)}
                                                        </Box>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <TablePagination
                            component="div"
                            count={serverPaged ? totalCount : filteredItems.length}
                            page={page}
                            onPageChange={handleChangePage}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            rowsPerPageOptions={[5, 10, 25]}
                            // Disables Next/Previous (and the rows-per-page select) while a page
                            // fetch is in flight, so repeatedly clicking Next doesn't queue up a
                            // burst of requests that then "fast-forward" through pages once they
                            // all resolve.
                            disabled={tableLoading}
                        />

                        {/* Message Reporting and Save Control */}
                        <Stack spacing={1} sx={{ mt: 2, width: "100%", maxWidth: 800 }}>
                            {message.type === "error" && (
                                <Typography color="error" sx={{ fontSize: 14 }}>
                                    {message.text}
                                </Typography>
                            )}

                            {message.type === "success" && (
                                <Typography color="success.main" sx={{ fontSize: 14 }}>
                                    {message.text}
                                </Typography>
                            )}

                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleSave}
                                disabled={saving || !unsavedChanges}
                                sx={{ width: 250 }}
                                startIcon={saving ? <CircularProgress size={18} thickness={5} /> : null}
                            >
                                {saving ? "Saving..." : "Save"}
                            </Button>
                        </Stack>
                    </Stack>
                </Paper>

                {/* Error Detail Dialog Overlay */}
                <Dialog open={!!errorDetail} onClose={() => setErrorDetail(null)} maxWidth="sm" fullWidth>
                    <DialogTitle sx={{ fontWeight: "bold", color: "error.main" }}>Error Details</DialogTitle>
                    <DialogContent dividers>
                        {errorDetail?.rowData && (
                            <Stack spacing={2}>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">Customer ID</Typography>
                                    <Typography variant="body1" sx={{ fontFamily: "monospace" }}>{errorDetail.rowData.itemCode}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">Customer Name</Typography>
                                    <Typography variant="body1">{errorDetail.rowData.itemName || "-"}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">Zamp Entity</Typography>
                                    <Typography variant="body1" sx={{ fontFamily: "monospace" }}>{errorDetail.rowData.taxCode || "-"}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">Error</Typography>
                                    <Typography variant="body1" color="error.main">{errorDetail.error}</Typography>
                                </Box>
                            </Stack>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setErrorDetail(null)}>Close</Button>
                    </DialogActions>
                </Dialog>

                {/* Backdrop Spinner Loader overlay during direct save sequence */}
                <Backdrop open={saving} className="zamp-backdrop-loader" sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}>
                    <CircularProgress size={48} thickness={4} />
                    <Typography variant="body1">Saving mappings...</Typography>
                </Backdrop>

                {/* Global Context Toast Notifications */}
                <Snackbar open={isOpen} autoHideDuration={3000} onClose={handleClose} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
                    <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
                        {alertMessage}
                    </Alert>
                </Snackbar>
            </Box>
        );
    }

    export default TaxCodeMapping;
